import React, { useCallback, useEffect, useState } from "react";
import style from "./hospitalSearch.module.scss";

// 병원 검색 프로토타입 (네이버 지역검색 API, dev 프록시 /api/naver/local 경유)
// ※ 운영 전 백엔드(Spring) 프록시로 이전 + 카카오/구글 Places 병합 필요.

interface NaverLocalItem {
  title: string;
  link: string;
  category: string;
  description: string;
  telephone: string;
  address: string;
  roadAddress: string;
  mapx: string;
  mapy: string;
}

const PRESETS = ["소아정신과", "청소년발달센터", "아동발달센터", "소아청소년 정신건강의학과"];

const stripTag = (s: string) => (s || "").replace(/<[^>]*>/g, "");

export function HospitalSearch() {
  const [query, setQuery] = useState("소아정신과");
  const [items, setItems] = useState<NaverLocalItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const search = useCallback(async (q: string) => {
    const term = q.trim();
    if (!term) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/naver/local?query=${encodeURIComponent(term)}`);
      const data = await res.json();
      setItems(data?.items ?? []);
    } catch (e) {
      setError(String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    search("소아정신과");
  }, [search]);

  const onPreset = (p: string) => {
    setQuery(p);
    search(p);
  };

  return (
    <div className={style.page}>
      <div style={S.wrap}>
        <h1 style={S.h1}>병원 바로 찾기</h1>
        <div style={S.sub}>K-PASS 결과 기반 전문 기관 검색 · 소아정신과 / 청소년발달센터</div>

        <div style={S.searchbar}>
          <input
            style={S.input}
            value={query}
            placeholder="예: 강남 소아정신과, 청소년발달센터"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search(query)}
          />
          <button style={S.go} onClick={() => search(query)}>검색</button>
        </div>

        <div style={S.presets}>
          {PRESETS.map((p) => (
            <span key={p} style={S.preset} onClick={() => onPreset(p)}>{p}</span>
          ))}
        </div>

        <div style={S.note}>
          ※ 네이버 지역검색은 1회 최대 5건 (프로토타입 한계). 실제 구현 시 카카오/구글 Places 병합으로 확장.
        </div>

        {loading && <div style={S.empty}>검색 중…</div>}
        {error && <div style={S.empty}>오류: {error}</div>}
        {!loading && !error && items.length === 0 && <div style={S.empty}>결과가 없습니다.</div>}

        {!loading && items.map((it, i) => {
          const name = stripTag(it.title);
          const addr = stripTag(it.roadAddress || it.address);
          const tel = stripTag(it.telephone);
          const mapUrl = `https://map.naver.com/v5/search/${encodeURIComponent(name)}`;
          const homeUrl = it.link
            ? it.link
            : `https://search.naver.com/search.naver?query=${encodeURIComponent(name)}`;
          return (
            <div key={i} style={S.card}>
              <div style={S.name}>{name}</div>
              <span style={S.cat}>{stripTag(it.category)}</span>
              <div style={S.addr}>{addr}</div>
              {tel && <div style={S.tel}>{tel}</div>}
              <div style={S.actions}>
                <a style={{ ...S.act, ...S.home }} href={homeUrl} target="_blank" rel="noopener noreferrer">
                  {it.link ? "홈페이지" : "정보 보기"}
                </a>
                <a style={{ ...S.act, ...S.map }} href={mapUrl} target="_blank" rel="noopener noreferrer">
                  지도보기
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const S: { [k: string]: React.CSSProperties } = {
  // .page(상단 패딩=헤더높이)는 scss 모듈에서 처리. 여기선 내부 컨텐츠만.
  wrap: { maxWidth: 720, margin: "0 auto", fontFamily: "-apple-system, 'Apple SD Gothic Neo', sans-serif", color: "#2b2640" },
  h1: { fontSize: 22, margin: "0 0 4px" },
  sub: { color: "#76718e", fontSize: 13, marginBottom: 20 },
  searchbar: { display: "flex", gap: 8, marginBottom: 12 },
  input: { flex: 1, padding: "12px 14px", border: "1px solid #ece9f5", borderRadius: 12, fontSize: 14, boxSizing: "border-box" },
  go: { padding: "12px 18px", border: 0, borderRadius: 12, background: "#6630ff", color: "#fff", fontWeight: 600, cursor: "pointer" },
  presets: { display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 },
  preset: { padding: "6px 12px", border: "1px solid #d9d2f5", borderRadius: 100, background: "#fff", color: "#6630ff", fontSize: 13, cursor: "pointer" },
  note: { fontSize: 12, color: "#a09bb5", marginBottom: 16 },
  card: { background: "#fff", border: "1px solid #ece9f5", borderRadius: 16, padding: 16, marginBottom: 12 },
  name: { fontSize: 16, fontWeight: 700 },
  cat: { display: "inline-block", marginTop: 4, padding: "3px 10px", borderRadius: 100, background: "#ede7ff", color: "#6630ff", fontSize: 12 },
  addr: { color: "#555", fontSize: 13, marginTop: 10, lineHeight: 1.5 },
  tel: { color: "#1f9d57", fontSize: 13, marginTop: 4 },
  actions: { display: "flex", gap: 8, marginTop: 12 },
  act: { textDecoration: "none", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 10 },
  home: { background: "#6630ff", color: "#fff" },
  map: { background: "#fff", border: "1px solid #d9d2f5", color: "#6630ff" },
  empty: { color: "#76718e", textAlign: "center", padding: "40px 0" },
};
